import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { APP_VERSION } from '../../config';
import {
  Printer,
  Calculator,
  AlertTriangle,
  BookOpen,
  ArrowRight,
  Target,
  Thermometer,
  Settings,
  CheckCircle,
  Clock,
  Zap,
  RefreshCw,
  ShoppingBag,
  MessageSquare,
  FolderKanban,
  Wrench,
  History,
  Star,
  Sparkles,
  Layers
} from 'lucide-react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import WorkspaceSetupBanner, { getWorkspaceStage, WorkspaceStatusLabel } from '@/components/WorkspaceSetupBanner';
import GatedToolCard from '@/components/GatedToolCard';

const Printing3DDashboard = () => {
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem('token');
  
  const [printers, setPrinters] = useState([]);
  const [recentCalculations, setRecentCalculations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [setupProgress, setSetupProgress] = useState({ hasPrinter: false, hasCalculation: false });
  
  // Phase 3.0: Track workspace setup completion state
  const [workspaceReady, setWorkspaceReady] = useState(false);
  const prevWorkspaceReady = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      
      try {
        const [printersRes, historyRes] = await Promise.all([
          axios.get(`${API}/printing3d/printers`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: [] })),
          axios.get(`${API}/printing3d/history?limit=5`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: [] }))
        ]);
        
        const printerList = printersRes.data || [];
        setPrinters(printerList);
        setRecentCalculations(historyRes.data || []);
        
        // Calculate setup progress
        const hasPrinter = printerList.length > 0;
        const hasCalculation = (historyRes.data || []).length > 0;
        
        setSetupProgress({ 
          hasPrinter, 
          hasCalculation,
          printerName: printerList[0]?.name || 'No printer'
        });
        
        // Phase 3.0: Set workspace ready state
        setWorkspaceReady(hasPrinter);
      } catch (error) {
        console.error('Failed to fetch 3D printing data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token]);

  // Setup progress steps - MUST be declared before useEffect that references progressPercent
  const progressSteps = setupProgress.hasPrinter ? [
    { label: 'Printer Added', done: setupProgress.hasPrinter, link: '/3d-printing/setup' },
    { label: 'First Calculation', done: setupProgress.hasCalculation, link: '/3d-printing/calculator' },
  ] : [
    { label: 'Set Up Printer', done: false, link: '/3d-printing/setup', primary: true },
    { label: 'First Calculation', done: false, link: '/3d-printing/calculator' },
  ];
  const progressPercent = Math.round((progressSteps.filter(s => s.done).length / progressSteps.length) * 100);
  
  // Phase 3.1: Show toast when workspace becomes operational
  useEffect(() => {
    if (workspaceReady && !prevWorkspaceReady.current && progressPercent >= 100) {
      toast.success('Printer validated. Workspace operational.', {
        duration: 4000,
        icon: <CheckCircle className="w-5 h-5 text-emerald-400" />
      });
    }
    prevWorkspaceReady.current = workspaceReady;
  }, [workspaceReady, progressPercent]);

  // Core tools matching Router Edition structure
  const coreTools = [
    {
      title: 'Troubleshooting',
      description: 'Step-by-step fixes for stringing, warping, adhesion, resin failures and more.',
      icon: AlertTriangle,
      link: '/3d-printing/troubleshooting'
    },
    {
      title: 'Material Guide',
      description: 'Temperature and settings guide for PLA, PETG, ABS, resins and more.',
      icon: BookOpen,
      link: '/3d-printing/materials'
    },
    {
      title: 'Slicer Guide',
      description: 'Layer height, infill, supports, speed, cooling and retraction explained.',
      icon: Layers,
      link: '/3d-printing/slicer-guide'
    },
    {
      title: 'Post Processing',
      description: 'Sanding, priming, painting, acetone smoothing, resin washing and UV curing.',
      icon: Sparkles,
      link: '/3d-printing/post-processing'
    }
  ];

  // Advanced tools matching Router Edition structure
  const advancedTools = [
    {
      title: 'My Printers',
      description: 'Manage your 3D printer profiles and settings.',
      icon: Printer,
      link: '/3d-printing/setup'
    },
    {
      title: 'Marketplace',
      description: 'Buy/sell 3D printing equipment and filaments.',
      icon: ShoppingBag,
      link: '/3d-printing/marketplace'
    },
    {
      title: 'My Equipment',
      description: 'Your printer profile and account settings.',
      icon: Wrench,
      link: '/profile'
    },
    {
      title: 'Community',
      description: 'Discuss print settings and troubleshooting tips.',
      icon: MessageSquare,
      link: '/community'
    }
  ];

  // Material quick tips for Active Profile widget
  const activePrinter = printers[0];

  return (
    <div 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" 
      data-testid="printing3d-dashboard"
      style={bgStyle(IMAGES.PRINTING3D_DASHBOARD)}
    >
      {/* Dark Overlay - Matching Router */}
      <div className="fixed inset-0 bg-black/80 -z-10" style={{ pointerEvents: 'none' }} />

      {/* Page Header - Matching Router */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22] mb-2">Foreman Console – Print Operations</div>
            <h1 
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#e67e22] tracking-tight uppercase"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Work Session
            </h1>
            <p className="mt-2 text-sm md:text-base text-[#e67e22] max-w-lg">
              Configure → calculate → print → finish. Welcome back, {user?.full_name}.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Phase 3.1: Workspace Setup Warning Banner or Operational Badge */}
        <WorkspaceSetupBanner 
          setupComplete={workspaceReady}
          completionPercent={progressPercent}
          setupLink="/3d-printing/setup"
          discipline="printer"
        />
        
        {/* Setup Progress Banner - Industrial Stage Display */}
        {workspaceReady && progressPercent < 100 && (
          <div className="bg-gradient-to-r from-[#e67e22]/20 to-[#e67e22]/5 backdrop-blur-sm border border-[#e67e22]/30 rounded-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-4">
                <Zap className="w-5 h-5 text-[#e67e22]" />
                <span className="text-white font-medium">
                  {setupProgress.printerName || 'Printer Setup'}
                </span>
                <WorkspaceStatusLabel percent={progressPercent} />
              </div>
              <div className="flex items-center gap-3">
                <Link to="/3d-printing/setup">
                  <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-black font-semibold h-7">
                    <ArrowRight className="w-3 h-3 mr-1" />
                    Continue Setup
                  </Button>
                </Link>
              </div>
            </div>
            <div className="w-full h-2 bg-black/70 rounded-full overflow-hidden mb-3">
              <div 
                className={`h-full bg-gradient-to-r from-[#e67e22] to-[#f39c12] transition-all duration-500 ${
                  progressPercent >= 100 ? 'shadow-[0_0_10px_rgba(16,185,129,0.5)]' : ''
                }`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="flex flex-wrap items-center gap-4">
              {progressSteps.map((step, i) => (
                <Link key={i} to={step.link} className="flex items-center gap-2 group">
                  {step.done ? (
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  ) : step.primary ? (
                    <div className="w-4 h-4 border-2 border-emerald-500 rounded-full bg-emerald-500/20 group-hover:bg-emerald-500/40 animate-pulse" />
                  ) : (
                    <div className="w-4 h-4 border-2 border-[#3a3a3a] rounded-full group-hover:border-[#e67e22]" />
                  )}
                  <span className={`text-sm ${
                    step.done 
                      ? 'text-green-400 line-through' 
                      : step.primary 
                        ? 'text-emerald-400 font-medium group-hover:text-emerald-300' 
                        : 'text-[#d6d6d6] group-hover:text-[#e67e22]'
                  }`}>
                    {step.label}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Active Printer Profile Widget - Matching Router */}
        {activePrinter && (
          <div className="bg-black/70 backdrop-blur-sm border border-[#3a3a3a]/50 rounded-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#e67e22]" />
                <span className="text-white font-medium">Active Printer Profile</span>
              </div>
              <Link to="/3d-printing/setup">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[#a3a3a3] hover:text-white h-7 px-2"
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Update Setup
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div className="bg-black/60 rounded p-2">
                <div className="text-xs text-[#b8b8b8]">Printer</div>
                <div className="text-sm text-white font-medium truncate">{activePrinter.name}</div>
              </div>
              <div className="bg-black/60 rounded p-2">
                <div className="text-xs text-[#b8b8b8]">Type</div>
                <div className="text-sm text-white font-medium capitalize">{activePrinter.motion_system || 'FDM'}</div>
              </div>
              <div className="bg-black/60 rounded p-2">
                <div className="text-xs text-[#b8b8b8]">Nozzle</div>
                <div className="text-sm text-white font-medium">{activePrinter.nozzle_diameter || 0.4}mm</div>
              </div>
              <div className="bg-black/60 rounded p-2">
                <div className="text-xs text-[#b8b8b8]">Extruder</div>
                <div className="text-sm text-white font-medium">
                  {activePrinter.direct_drive ? 'Direct' : 'Bowden'}
                </div>
              </div>
              <div className="bg-black/60 rounded p-2">
                <div className="text-xs text-[#b8b8b8]">Max Nozzle</div>
                <div className="text-sm text-white font-medium">{activePrinter.max_nozzle_temp || 260}°C</div>
              </div>
              <div className="bg-black/60 rounded p-2">
                <div className="text-xs text-[#b8b8b8]">Bed</div>
                <div className="text-sm text-white font-medium">{activePrinter.heated_bed ? `${activePrinter.max_bed_temp || 100}°C` : 'Unheated'}</div>
              </div>
            </div>
          </div>
        )}

        {/* Core tools - Matching Router */}
        <div className="bg-black/70 backdrop-blur-sm border border-[#3a3a3a]/50 rounded-sm p-6">
          <div className="mb-4">
            <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22]">Core tools</div>
            <div className="text-sm text-[#e0e0e0] mt-1">
              Operational tools used before and after a print.
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {/* Troubleshooting - Left - Gated */}
            <GatedToolCard
              to="/3d-printing/troubleshooting"
              setupComplete={workspaceReady}
              className="bg-black/60 border border-[#3a3a3a]/50 p-4 rounded-sm hover:border-[#e67e22]/50 transition-all group"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-black/60 border border-[#3a3a3a]/50 flex items-center justify-center shrink-0 group-hover:border-[#e67e22]/50 transition-colors">
                  <AlertTriangle className="w-5 h-5 text-[#e67e22]" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#e67e22] group-hover:text-[#e67e22] transition-colors">
                    Troubleshooting
                  </div>
                  <div className="text-xs text-[#e0e0e0] mt-1">
                    Step-by-step fixes for stringing, warping, adhesion and more.
                  </div>
                </div>
              </div>
            </GatedToolCard>
            
            {/* Empty middle space */}
            <div className="hidden md:block"></div>
            
            {/* Material Guide - Right - Not gated (educational) */}
            <Link
              to="/3d-printing/materials"
              className="bg-black/60 border border-[#3a3a3a]/50 p-4 rounded-sm hover:border-[#e67e22]/50 transition-all group"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-black/60 border border-[#3a3a3a]/50 flex items-center justify-center shrink-0 group-hover:border-[#e67e22]/50 transition-colors">
                  <BookOpen className="w-5 h-5 text-[#e67e22]" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#e67e22] group-hover:text-[#e67e22] transition-colors">
                    Material Guide
                  </div>
                  <div className="text-xs text-[#e0e0e0] mt-1">
                    Temperature and settings guide for PLA, PETG, ABS and more.
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* Support & advanced - Matching Router */}
        <div className="bg-black/70 backdrop-blur-sm border border-[#3a3a3a]/50 rounded-sm p-6">
          <div className="mb-4">
            <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22]">Support & advanced</div>
            <div className="text-sm text-[#e0e0e0] mt-1">
              Secondary tools for sourcing, equipment, and discussion.
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {advancedTools.map((tool, index) => {
              const Icon = tool.icon;
              return (
                <Link
                  key={index}
                  to={tool.link}
                  className="bg-black/60 border border-[#3a3a3a]/50 p-4 rounded-sm hover:border-[#e67e22]/50 transition-all group"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-black/60 border border-[#3a3a3a]/50 flex items-center justify-center shrink-0 group-hover:border-[#e67e22]/50 transition-colors">
                      <Icon className="w-5 h-5 text-[#e67e22]" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-white group-hover:text-[#e67e22] transition-colors">
                        {tool.title}
                      </div>
                      <div className="text-xs text-[#e0e0e0] mt-1">
                        {tool.description}
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Recent Activity Feed - Matching Router */}
        <div className="bg-black/70 backdrop-blur-sm border border-[#3a3a3a]/50 rounded-sm p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22] flex items-center gap-2">
                <History className="w-4 h-4" />
                Recent Activity
              </div>
              <div className="text-sm text-[#e0e0e0] mt-1">
                Pick up where you left off
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Recent Calculations */}
            <div className="bg-black/70 border border-[#3a3a3a]/30 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <Calculator className="w-4 h-4 text-[#3b82f6]" />
                <span className="text-sm font-medium text-white">Recent Calculations</span>
              </div>
              {recentCalculations.length > 0 ? (
                <div className="space-y-2">
                  {recentCalculations.slice(0, 3).map((calc, i) => (
                    <Link
                      key={i}
                      to="/3d-printing/calculator"
                      className="block p-2 bg-black/20 rounded hover:bg-[#3b82f6]/10 hover:border-[#3b82f6]/30 border border-transparent transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-xs font-medium text-white truncate uppercase">{calc.material}</div>
                          <div className="text-xs text-[#a3a3a3]">{calc.nozzle_temp}°C · {calc.print_speed}mm/s</div>
                        </div>
                        <div className="text-xs text-[#6b7280] flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(calc.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-[#6b7280] py-4 text-center">
                  No calculations yet.{' '}
                  <Link to="/3d-printing/calculator" className="text-[#3b82f6] hover:underline">Start one</Link>
                </div>
              )}
            </div>

            {/* My Printers */}
            <div className="bg-black/70 border border-[#3a3a3a]/30 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <Printer className="w-4 h-4 text-[#f59e0b]" />
                <span className="text-sm font-medium text-white">My Printers</span>
              </div>
              {printers.length > 0 ? (
                <div className="space-y-2">
                  {printers.slice(0, 3).map((printer, i) => (
                    <Link
                      key={i}
                      to="/3d-printing/setup"
                      className="block p-2 bg-black/20 rounded hover:bg-[#f59e0b]/10 hover:border-[#f59e0b]/30 border border-transparent transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-xs font-medium text-[#f59e0b] truncate">{printer.name}</div>
                          <div className="text-xs text-[#a3a3a3]">{printer.brand} {printer.model}</div>
                        </div>
                        <Star className="w-3 h-3 text-[#f59e0b]" />
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-[#6b7280] py-4 text-center">
                  No printers configured.{' '}
                  <Link to="/3d-printing/setup" className="text-[#f59e0b] hover:underline">Add one</Link>
                </div>
              )}
            </div>

            {/* Quick Material Reference */}
            <div className="bg-black/70 border border-[#3a3a3a]/30 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <Thermometer className="w-4 h-4 text-[#10b981]" />
                <span className="text-sm font-medium text-white">Quick Reference</span>
              </div>
              <div className="space-y-2">
                {[
                  { material: 'PLA', temp: '200-210°C', bed: '60°C' },
                  { material: 'PETG', temp: '230-240°C', bed: '80°C' },
                  { material: 'ABS', temp: '240-250°C', bed: '100°C' },
                ].map((mat, i) => (
                  <div key={i} className="p-2 bg-black/20 rounded">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-[#10b981]">{mat.material}</span>
                      <span className="text-xs text-[#a3a3a3]">{mat.temp} · Bed {mat.bed}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Bar - Matching Router */}
      <div className="bg-black/70 backdrop-blur-sm border border-[#3a3a3a]/50 rounded-sm p-4 mt-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-[#27ae60] rounded-full animate-pulse" />
              <span className="text-xs text-[#d6d6d6]">System online</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-[#27ae60] rounded-full" />
              <span className="text-xs text-[#d6d6d6]">Database connected</span>
            </div>
          </div>
          <div className="text-xs font-mono text-[#a3a3a3]">3D PRINTING EDITION v{APP_VERSION}</div>
        </div>
      </div>
    </div>
  );
};

export default Printing3DDashboard;
